import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { Grid, LinearProgress, styled, Typography } from '@mui/material';

import { apiVideoLibraryProvider, VideoManagerState } from '../../store/videoManagerSlice';
import { OrganisationContext } from '../../App';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../index';
import PaginationTable from '../Pagination';
import VideoMenu from './VideoMenu';
import { EditVideoRequest } from '../modal/VideoEditRequest';
import { ReUploadVideo } from '../modal/VideoReUpload';
import { ChildVideo, VideoManagerItem, VideoProps } from '../types/videos';
import { getApiBaseUrl } from '../../utils/env';

import { VideoBasicPreview } from '../modal/VideoBasicPreview';
import { VideoClip } from '../modal/VideoClip';
import Moment from 'react-moment';
import { VIDEO_STATUS } from '../types/videoStatus';
import { VideoCard } from './thumbnail/VideoCard';
import { VideoTitle } from './VideoTabContent';

const GridRow = styled(Grid)`
    position: relative;

    :not(:first-child) {
        &:after {
            content: ' ';
            position: absolute;
            width: 100%;
            height: 1px;
            background-color: rgba(224, 224, 224, 1);
            top: 10px;
            left: 0;
        }
    }
`;

const GridRoot = styled(Grid)`
    position: relative;

    &:after {
        content: ' ';
        position: absolute;
        width: 100%;
        height: 1px;
        background-color: rgba(224, 224, 224, 1);
        bottom: 0;
        left: 0;
    }
`;

export default function VideoToActionTabContent() {
  const dispatch = useDispatch();
  const { selectedOrganisation } = useContext(OrganisationContext);
  const {
    pagination, videoFilterFormat, videos, busy, searchText
  } = useSelector<RootState, VideoManagerState>((state) => state.videoManager);
  const [editVideoRequest, setEditVideoRequest] = useState<ChildVideo>();
  const [reUploadVideoRequest, setReUploadVideoRequest] = useState<VideoManagerItem>();
  const [previewVideo, setPreviewVideo] = useState<VideoProps>();
  const [clipVideo, setClipVideo] = useState<{ video: VideoManagerItem, format: string }>();

  const updateList = () => {
    dispatch(apiVideoLibraryProvider({
      publisher: selectedOrganisation,
      limit: pagination.itemsPerPage,
      page: pagination.currentPage,
      filterByFormat: videoFilterFormat,
      search: searchText,
      toAction: true,
    }));
  };

  useEffect(() => {
    dispatch({ type: 'RESET_PAGINATION' });

    let timer: NodeJS.Timeout;
    const refreshList = () => {
      return setTimeout(() => {
        clearTimeout(timer);
        updateList();
        timer = refreshList();
      }, 20000);
    };

    timer = refreshList();
    return () => {
      dispatch({ type: 'RESET_PAGINATION' });
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganisation]);

  useEffect(() => {
    if (!selectedOrganisation) return;

    updateList();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.itemsPerPage, pagination.currentPage, videoFilterFormat, selectedOrganisation, searchText]);

  const keepVideo = async (video: VideoManagerItem) => {
    await axios.post(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/manage/toggle-status`, {
      uuid: video.uuid,
      next_status: VIDEO_STATUS.PENDING_APPROVAL
    });
    updateList();
  };

  const deleteSource = async (video: VideoManagerItem) => {
    await axios.delete(`${getApiBaseUrl()}/${selectedOrganisation}/video-library/source/${video.uuid}`);
    updateList();
  };

  const download = async (uuid: string, rightsholder: string) => {
    const { data } = await axios.get(`${getApiBaseUrl()}/video-clip/${rightsholder}/download-clip`, {
      params: {
        uuid
      }
    });
    window.location.href = data;
  };

  return <>
    {clipVideo && <VideoClip
      format={clipVideo.format}
      title={'Clip'}
      open={Boolean(clipVideo)} handleClose={() => {
        setClipVideo(undefined);
        updateList();
      }}
      video={clipVideo.video}
    />}
    {previewVideo &&
      <VideoBasicPreview open={Boolean(previewVideo)} handleClose={() => setPreviewVideo(undefined)}
                         video={previewVideo}/>}
    {editVideoRequest && <EditVideoRequest open={Boolean(editVideoRequest)}
                                           handleClose={() => setEditVideoRequest(undefined)}
                                           video={editVideoRequest}/>}
    {reUploadVideoRequest && <ReUploadVideo open={Boolean(reUploadVideoRequest)}
                                            handleClose={() => setReUploadVideoRequest(undefined)}
                                            video={reUploadVideoRequest}/>}
    <GridRoot container sx={{ m: 2, pb: 2 }}>
      {videos.length === 0 && <Typography>No videos to action</Typography>}
      {videos.length > 0 && videos.map(video => (
        <GridRow key={video.uuid} item xs={12} sx={{ pt: 4 }}>
          <Grid container wrap="nowrap" spacing={2}>
            <Grid item xs={3} sx={{ minWidth: '300px' }}>
              <VideoCard
                video={video}
                onClick={() => setPreviewVideo({
                  parent_media_id: '',
                  parent_title: video.title,
                  parent_title_translated: '',
                  has_variant: false,
                  can_manage_video: false,
                  summary: video.summary,
                  media_id: video.media_id,
                  preview_uri: video.preview_uri,
                  rightsholder: video.domain,
                  video_type: 'pixels',
                  format: video.format,
                  title: video.title,
                  publish_date: new Date(video.publish_date),
                  transcripts: [],
                  uuid: video.uuid,
                  opus_title: '',
                  opus_description: '',
                  opus_clip_genre: '',
                  opus_hashtags: [],
                })}
                menu={<VideoMenu
                  onDownloadMp4={() => download(video.uuid, video.domain)}
                  onClip={(format) => setClipVideo({ video, format })}
                  onDelete={async () => deleteSource(video)}
                  reClip={false}
                  video={video}
                  onKeepVideo={() => keepVideo(video)}
                  onApprove={() => console.warn('approve not implemented')}
                  onReUpload={() => setReUploadVideoRequest(video)}
                />}
              />
              <VideoTitle>{video.title}</VideoTitle>
              <Typography fontSize={13}>Published date: <Moment
                format="D MMMM YYYY">{video.publish_date}</Moment></Typography>
              <Typography fontSize={13}>Ingested at: <Moment
                format="D MMMM YYYY">{video.created_at}</Moment></Typography>
            </Grid>
          </Grid>
        </GridRow>))}
    </GridRoot>
    {busy && <LinearProgress color="success"/>}
    <PaginationTable
      defaultSize={20}
      busy={busy}
      startPage={pagination.currentPage - 1}
      count={pagination.totalItems}
      onChange={page =>
        dispatch({
          type: 'SET_PAGINATION',
          payload: {
            currentPage: page + 1,
          },
        })
      }
      updateRowPerPage={totalPerPage => {
        dispatch({
          type: 'SET_PAGINATION',
          payload: {
            currentPage: 1,
            itemsPerPage: totalPerPage,
          },
        });
      }}
    />
  </>;
}